/**
 * SSE consumer for /api/v1/chat/stream.
 *
 * We can't use the browser's `EventSource` API because it doesn't
 * support custom headers (so we'd have no way to send the Authorization
 * bearer). Instead we open a `fetch` POST and read the body as a
 * `ReadableStream`, parsing SSE frames manually. The format is the
 * same one the BE emits via axum::response::sse:
 *
 *   event: chunk
 *   data: {"kind":"delta","content":"hi","reasoning_content":""}
 *
 *   event: done
 *   data: {"kind":"done"}
 *
 * Frames are separated by a blank line. The parser also drops `: ping`
 * comment-style keep-alive lines emitted every 15s by the BE.
 */
import type { ChatChunk, ChatRequest } from "@/api/generated";

export interface SsePostOptions {
  /** Base URL for the API; defaults to current origin. */
  baseUrl?: string;
  /** Bearer token for the Authorization header. */
  token?: string | null;
  /** AbortSignal passed through to fetch. */
  signal?: AbortSignal;
}

export type SseFrame = { event: string; data: string };

/**
 * Parse a stream of UTF-8 bytes into SSE frames. Emits one frame per
 * blank-line-terminated record. Internal — exported for tests.
 */
export async function* parseSseFrames(
  source: ReadableStream<Uint8Array>,
): AsyncGenerator<SseFrame, void, void> {
  const reader = source.getReader();
  const decoder = new TextDecoder("utf-8");
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    // Normalize CRLF → LF on the whole buffer AFTER append: axum emits
    // LF-only, but any proxy in front (Cloudflare Tunnel is on the
    // go-live path) may re-emit CRLF, and normalizing per-chunk would
    // miss a \r|\n pair split across a chunk boundary. Without this,
    // every frame boundary is missed and chat hangs silently.
    buf = buf.replace(/\r\n/g, "\n");

    // Frames are separated by `\n\n` (post-normalization).
    let idx: number;
    // biome-ignore lint/suspicious/noAssignInExpressions: classic SSE parser shape
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const raw = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const frame = parseFrame(raw);
      if (frame) yield frame;
    }
  }
  // Drain trailing partial frame, if any.
  if (buf.trim().length > 0) {
    const frame = parseFrame(buf);
    if (frame) yield frame;
  }
}

function parseFrame(raw: string): SseFrame | null {
  let event = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line.startsWith(":")) continue; // comment / keep-alive
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  }
  if (dataLines.length === 0) return null;
  return { event, data: dataLines.join("\n") };
}

/**
 * Open the chat stream and yield decoded `ChatChunk`s. Caller iterates
 * with `for await (const chunk of streamChat(req)) { ... }`.
 *
 * Errors:
 *   - HTTP 4xx (validation, auth) → throws an Error with message from
 *     the JSON body. Caller renders inline.
 *   - In-stream `event: error` → yields a `ChatChunk.Error` then ends.
 */
export async function* streamChat(
  req: ChatRequest,
  opts: SsePostOptions = {},
): AsyncGenerator<ChatChunk, void, void> {
  const baseUrl = opts.baseUrl ?? "";
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  const res = await fetch(`${baseUrl}/api/v1/chat/stream`, {
    method: "POST",
    headers,
    body: JSON.stringify(req),
    signal: opts.signal,
  });

  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    const reason =
      typeof detail === "object" &&
      detail !== null &&
      "message" in detail &&
      typeof (detail as { message: unknown }).message === "string"
        ? (detail as { message: string }).message
        : `HTTP ${res.status} ${res.statusText}`;
    throw new Error(reason);
  }

  if (!res.body) {
    throw new Error("response had no body");
  }

  for await (const frame of parseSseFrames(res.body)) {
    if (frame.event === "error") {
      // The error frame's body is already shaped like ChatChunk.Error.
      try {
        yield JSON.parse(frame.data) as ChatChunk;
      } catch {
        yield {
          kind: "error",
          code: "internal",
          message: "malformed error frame",
        } as ChatChunk;
      }
      return;
    }
    if (frame.event === "chunk" || frame.event === "done") {
      try {
        yield JSON.parse(frame.data) as ChatChunk;
      } catch {
        // Skip unparseable frames rather than failing the stream.
      }
    }
    // Other event names are ignored (forward-compat).
  }
}
